/**
 * Admin API — moderation, user management, analytics, audit.
 * Uses service_role-level access via RLS "moderator" policies.
 */

import supabase from "../config/supabase";
import { listMemberProfiles } from "./users";
import { invokeFunction } from "./functionErrors";

const adminApi = {
  // Reads the AI-triaged moderation queue. The AI has already recommended
  // approve/flag for each item; the admin confirms. Items are returned with the
  // AI metadata the moderation screen renders (recommendation, rationale,
  // confidence, flag reasons) plus the submitter's name.
  async listPendingItems(params = {}) {
    let query = supabase
      .from("moderation_queue")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (params.limit) query = query.limit(params.limit);

    const { data: rows, error } = await query;
    if (error) return { success: false, error: error.message };

    // Resolve submitter names in one batch.
    const submitterIds = [...new Set((rows || []).map((r) => r.submitted_by).filter(Boolean))];
    let nameById = {};
    if (submitterIds.length) {
      const { data: users } = await supabase
        .from("users")
        .select("id, first_name, last_name")
        .in("id", submitterIds);
      nameById = Object.fromEntries(
        (users || []).map((u) => [u.id, `${u.first_name || ""} ${u.last_name || ""}`.trim()])
      );
    }

    const serviceQueueIds = (rows || []).filter(row => row.entity_type === "service").map(row => row.id);
    let checks = [];
    let duplicateCheckError = null;
    if (serviceQueueIds.length) {
      try {
        const result = await supabase.rpc("admin_service_duplicate_checks", {p_queue_ids: serviceQueueIds});
        checks = result.data || [];
        duplicateCheckError = result.error ? "Duplicate check unavailable. Review manually before approving." : null;
      } catch (_) {
        duplicateCheckError = "Duplicate check unavailable. Review manually before approving.";
      }
    }
    const checkByQueue = new Map(checks.map(check => [check.queue_id, check]));

    const items = (rows || []).map((r) => ({
      ...r,
      submitted_by_name: nameById[r.submitted_by] || "Member",
      duplicate_check: checkByQueue.get(r.id) || null,
      duplicate_check_error: r.entity_type === "service" ? duplicateCheckError : null,
    }));

    return { success: true, data: { items } };
  },

  async approveItem(queueId) {
    return invokeFunction(
      supabase,
      "admin-actions",
      { body: { action: "approve", queue_id: queueId } },
      "Could not approve that listing. Please try again.",
    );
  },

  async rejectItem(queueId, reason) {
    return invokeFunction(
      supabase,
      "admin-actions",
      { body: { action: "reject", queue_id: queueId, reason } },
      "Could not reject that listing. Please try again.",
    );
  },

  async listUsers(params = {}) {
    return listMemberProfiles({
      role: params.role,
      limit: params.limit,
    });
  },

  async getAnalytics() {
    const [users, services, jobs, events] = await Promise.all([
      supabase.from("users").select("id", { count: "exact", head: true }),
      supabase.from("services").select("*", { count: "exact", head: true }),
      supabase.from("job_postings").select("*", { count: "exact", head: true }),
      supabase.from("events").select("*", { count: "exact", head: true }),
    ]);

    return {
      success: true,
      data: {
        total_users: users.count || 0,
        total_services: services.count || 0,
        total_jobs: jobs.count || 0,
        total_events: events.count || 0,
      },
    };
  },

  // Phone checks — see 7_OWN_OUR_DATA.md.
  //
  // A member who re-enters a scraped listing's number from their own phone
  // either agrees with what we hold or doesn't. A disagreement is deliberately
  // not written over the listing, so it sits here until somebody looks. Without
  // this screen those pile up invisibly, which is the whole reason the mismatch
  // outcome exists rather than just overwriting.
  async listPhoneMismatches() {
    const { data: rows, error } = await supabase
      .from("listing_phone_submissions")
      .select("id, service_id, user_id, submitted, created_at")
      .eq("outcome", "mismatch")
      .order("created_at", { ascending: false });
    if (error) return { success: false, error: error.message };
    if (!rows?.length) return { success: true, data: [] };

    // Two batched lookups rather than one embed: the foreign keys point at
    // services and users, and the restrictive test-content policy on services
    // (050) makes an embed easier to get wrong than two plain reads.
    const serviceIds = [...new Set(rows.map((r) => r.service_id))];
    const userIds = [...new Set(rows.map((r) => r.user_id))];

    const [{ data: services }, { data: users }] = await Promise.all([
      supabase.from("services").select("id, title, mobile, source").in("id", serviceIds),
      supabase.from("users").select("id, first_name, last_name").in("id", userIds),
    ]);

    const svcById = Object.fromEntries((services || []).map((s) => [s.id, s]));
    const userById = Object.fromEntries((users || []).map((u) => [u.id, u]));

    return {
      success: true,
      data: rows.map((r) => {
        const svc = svcById[r.service_id] || {};
        const u = userById[r.user_id] || {};
        return {
          id: r.id,
          serviceId: r.service_id,
          title: svc.title || "Listing",
          held: svc.mobile || "",
          submitted: r.submitted,
          source: svc.source || null,
          memberName: `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Member",
          createdAt: r.created_at,
        };
      }),
    };
  },

  // Take the member's number over the one we hold. This is the only place a
  // mismatch can change a listing, and it flips the row to member-sourced for
  // the same reason a match does: the number is now one a member supplied.
  async acceptMemberPhone(submission) {
    const { error } = await supabase
      .from("services")
      .update({
        mobile: submission.submitted,
        source: submission.source === "google" ? "member" : submission.source,
      })
      .eq("id", submission.serviceId);
    if (error) return { success: false, error: error.message };

    const { error: delError } = await supabase
      .from("listing_phone_submissions")
      .delete()
      .eq("id", submission.id);
    if (delError) return { success: false, error: delError.message };

    return { success: true };
  },

  // Keep what we hold. The submission goes, so the queue doesn't show it again.
  async dismissPhoneSubmission(submissionId) {
    const { error } = await supabase
      .from("listing_phone_submissions")
      .delete()
      .eq("id", submissionId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  // How far the directory has moved off Google's data. The point of the source
  // column is that this number can be watched going down.
  async listingSourceCounts() {
    const sources = ["google", "member", "owner", "admin"];
    const results = await Promise.all(
      sources.map((s) =>
        supabase.from("services").select("id", { count: "exact", head: true }).eq("source", s),
      ),
    );
    return {
      success: true,
      data: Object.fromEntries(sources.map((s, i) => [s, results[i].count || 0])),
    };
  },

  async getAuditLog(params = {}) {
    let query = supabase.from("audit_logs").select("*").order("created_at", { ascending: false });
    if (params.limit) query = query.limit(params.limit);
    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },
};

export default adminApi;
